import React from "react";
import { CalendarCheck, Target, Repeat, Layers, Trophy, Compass, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const WEEKLY_TIPS = [
  {
    icon: Layers,
    title: "Stack it onto something you already do",
    intro: "The fastest way to make a habit stick is to anchor it to an existing routine — this is called habit stacking.",
    highlight: 'After I pour my morning coffee, I will write down one priority for the day.',
    outro: "Your current routine is a series of reliable cues. Borrow one, and let it trigger your new habit automatically.",
  },
  {
    icon: Target,
    title: "Make it embarrassingly small",
    intro: "Motivation fades; friction wins. Shrink the habit until starting is almost effortless.",
    highlight: 'Not "train for an hour" → "put my kit on." Not "meditate 10 minutes" → "take three breaths."',
    outro: "Once you've started, continuing is easy. The win this week is simply showing up, not the size of the effort.",
  },
  {
    icon: Repeat,
    title: "Never miss twice",
    intro: "One missed day is an accident. Two in a row is the start of a new (worse) habit.",
    highlight: "Missing once is fine — just make sure the very next day, you show up again, even at half effort.",
    outro: "Consistency isn't perfection. It's how quickly you return after a slip. Protect the comeback, not the streak.",
  },
  {
    icon: Compass,
    title: "Design your environment",
    intro: "You don't rise to the level of your willpower — you fall to the level of your surroundings.",
    highlight: "Make the good habit obvious and the bad one invisible: shoes by the door, phone in another room.",
    outro: "Change the cues around you this week and the behaviour follows with far less effort.",
  },
  {
    icon: Trophy,
    title: "Focus on identity, not outcomes",
    intro: "Lasting change comes from who you believe you are, not the goal you chase.",
    highlight: 'Every rep is a vote: "I am someone who trains," "I am someone who follows through."',
    outro: "Ask not \"what do I want to achieve?\" but \"who do I want to become?\" Then act like that person once today.",
  },
  {
    icon: CalendarCheck,
    title: "Reflect before you plan",
    intro: "A five-minute weekly review beats a perfect plan you never revisit.",
    highlight: "Ask: What worked? What got in the way? What is the one small change for the week ahead?",
    outro: "Progress compounds when you learn from last week instead of repeating it. Book five minutes today.",
  },
];

export default function WeeklyTipModal({ open, onClose, tipIndex = 0 }) {
  const tip = WEEKLY_TIPS[((tipIndex % WEEKLY_TIPS.length) + WEEKLY_TIPS.length) % WEEKLY_TIPS.length];
  const Icon = tip.icon || Sparkles;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#12141C] border border-emerald-500/30 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="weekly-tip-modal">
        <DialogHeader>
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-400 flex items-center justify-center glow-emerald mb-3">
            <Icon className="w-7 h-7 text-black" />
          </div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-400 mb-1">This week's coaching</p>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">{tip.title}</DialogTitle>
          <DialogDescription className="text-white">A quick, practical habit tip from Smart Performance Coaching.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm leading-relaxed text-white mt-1">
          <p className="m-0">{tip.intro}</p>

          <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
            <Sparkles className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
            <p className="m-0 font-medium text-emerald-100">{tip.highlight}</p>
          </div>

          <p className="m-0">{tip.outro}</p>
        </div>

        <div className="mt-6">
          <button
            data-testid="weekly-tip-close-button"
            onClick={onClose}
            className="w-full h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 text-black font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
          >
            <Sparkles className="w-5 h-5" /> Got it — let's go
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
